// Art Director → Cast (Jul 2026) — DEV-only.
//
// The #3 architecture: the AI owns the CONCEPT + TONE, the recipe pool owns the
// CRAFT. Enter a product → an LLM writes a product-APPROPRIATE art direction
// (scene / mood / lighting / palette + an AVOID list), then CASTS recipes from
// the full pool by craft-fit, ignoring product_type. Read-only, NO render.
// Eyeball whether the casting picks the right craft before wiring it to render.
//
// DEV-only: /api/dev/art-director-cast is an auth-skiplist route; local curation.

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, getAuthHeaders } from '@/lib/api';

interface CastRecipe {
  id: string;
  vibe: string;
  vibe_slug: string;
  product_types: string[];
  model_relation: string | null;
  requires_model: boolean;
  preview_url: string | null;
  fit: number | null;
  why: string;
  aesthetic_summary: string;
}
interface ArtDirection {
  angle?: string;
  scene: string;
  setting_type: string;
  mood: string[];
  lighting: string;
  palette_intent: string;
  composition: string;
  energy: string;
  family?: string;
  avoid: string[];
  rationale: string;
}
interface PairInfo {
  family: string;
  picked_from_family: string;
  fallback: boolean;
  device_name: string;
  device_desc: string;
  light_handling: string;
  source_id: string;
  source_vibe: string;
  source_vibe_slug: string | null;
  preview_url: string | null;
  has_model: boolean;
}
interface PairRender {
  img: string;
  provider: string | null;
  cost: number | null;
  family: string;
  pair: PairInfo;
}
interface Concept {
  art_direction: ArtDirection;
  cast: CastRecipe[];
}
interface ConceptsResponse {
  concepts: Concept[];
  pool_size: number;
  brand_dna_used: boolean;
  saw_product_image?: boolean;
  scraped?: { name: string; description: string; image_b64: string; source_url: string } | null;
  product?: { name: string; description: string };
}
interface CastResponse {
  art_direction: ArtDirection;
  cast: CastRecipe[];
  pool_size?: number | null;
  brand_dna_used: boolean;
  saw_product_image?: boolean;
  scraped?: { name: string; description: string; image_b64: string; source_url: string } | null;
  product?: { name: string; description: string };
}

export default function ArtDirectorCastPage() {
  const [productUrl, setProductUrl] = useState('');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('');
  const [brandId, setBrandId] = useState('');
  const [topK, setTopK] = useState(8);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CastResponse | null>(null);
  const [renderingId, setRenderingId] = useState<string | null>(null);
  const [steer, setSteer] = useState(true);
  const [renders, setRenders] = useState<Record<string, { img: string; provider: string | null; cost: number | null; steered: boolean }>>({});
  const [conceptsN, setConceptsN] = useState(4);
  const [conceptsRunning, setConceptsRunning] = useState(false);
  const [conceptsResult, setConceptsResult] = useState<ConceptsResponse | null>(null);
  const [conceptRenderingIdx, setConceptRenderingIdx] = useState<number | null>(null);
  const [conceptRenders, setConceptRenders] = useState<Record<number, { img: string; provider: string | null; cost: number | null; steered: boolean }>>({});
  // pair pick (lean architecture): AD owns the scene, one recipe's device+light grafted in
  const [pairRunning, setPairRunning] = useState(false);
  const [pairResult, setPairResult] = useState<PairRender | null>(null);
  const [pairExclude, setPairExclude] = useState<string[]>([]);

  const run = useCallback(async () => {
    if (!name.trim() && !productUrl.trim()) {
      setError('Enter a product URL or a product name.');
      return;
    }
    setError(null);
    setRunning(true);
    setResult(null);
    setConceptsResult(null);
    setRenders({});
    setPairResult(null);
    setPairExclude([]);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/art-director-cast`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          product_url: productUrl.trim() || undefined,
          product_name: name.trim() || undefined,
          product_description: desc.trim() || undefined,
          product_category: category.trim() || undefined,
          brand_id: brandId.trim() || undefined,
          top_k: topK,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 300)}`);
      setResult((await resp.json()) as CastResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }, [productUrl, name, desc, category, brandId, topK]);

  // DIRECT only (fast) — write the art direction, skip the pool load + cast.
  // The pair pick draws from the baked local menu, so it doesn't need the cast.
  const runDirect = useCallback(async () => {
    if (!name.trim() && !productUrl.trim()) {
      setError('Enter a product URL or a product name.');
      return;
    }
    setError(null);
    setRunning(true);
    setResult(null);
    setConceptsResult(null);
    setRenders({});
    setPairResult(null);
    setPairExclude([]);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/art-director-direct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          product_url: productUrl.trim() || undefined,
          product_name: name.trim() || undefined,
          product_description: desc.trim() || undefined,
          product_category: category.trim() || undefined,
          brand_id: brandId.trim() || undefined,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 300)}`);
      setResult((await resp.json()) as CastResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }, [productUrl, name, desc, category, brandId]);

  const renderPick = useCallback(async (c: CastRecipe) => {
    const img = result?.scraped?.image_b64;
    if (!img) return;
    setRenderingId(c.id);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/art-director-render`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          vibe_slug: c.vibe_slug,
          recipe_id: c.id,
          product_image_b64: img,
          product_name: result?.product?.name || '',
          brand_id: brandId.trim() || undefined,
          steer,
          art_direction: steer ? result?.art_direction : undefined,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
      const data = await resp.json();
      setRenders((prev) => ({ ...prev, [c.id]: { img: data.image_b64, provider: data.provider, cost: data.cost, steered: !!data.steered } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRenderingId(null);
    }
  }, [result, brandId, steer]);

  // PAIR pick (lean): AD owns the whole scene; graft ONE recipe's device+light.
  const renderPair = useCallback(async (reroll: boolean) => {
    const img = result?.scraped?.image_b64;
    const adx = result?.art_direction;
    if (!img || !adx) return;
    setPairRunning(true);
    setError(null);
    const exclude = reroll && pairResult ? [...pairExclude, pairResult.pair.source_id] : [];
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/art-director-pair-render`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          art_direction: adx,
          product_image_b64: img,
          product_name: result?.product?.name || '',
          brand_id: brandId.trim() || undefined,
          exclude_ids: exclude.length ? exclude : undefined,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
      const data = await resp.json();
      setPairResult({
        img: data.image_b64,
        provider: data.provider,
        cost: data.cost,
        family: data.family,
        pair: data.pair,
      });
      setPairExclude(exclude);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPairRunning(false);
    }
  }, [result, brandId, pairResult, pairExclude]);

  const runConcepts = useCallback(async () => {
    if (!name.trim() && !productUrl.trim()) {
      setError('Enter a product URL or a product name.');
      return;
    }
    setError(null);
    setConceptsRunning(true);
    setConceptsResult(null);
    setResult(null);
    setConceptRenders({});
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/art-director-concepts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          product_url: productUrl.trim() || undefined,
          product_name: name.trim() || undefined,
          product_description: desc.trim() || undefined,
          product_category: category.trim() || undefined,
          brand_id: brandId.trim() || undefined,
          n: conceptsN,
          top_k: 3,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 300)}`);
      setConceptsResult((await resp.json()) as ConceptsResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setConceptsRunning(false);
    }
  }, [productUrl, name, desc, category, brandId, conceptsN]);

  const renderConcept = useCallback(async (idx: number, concept: Concept) => {
    const img = conceptsResult?.scraped?.image_b64;
    const top = concept.cast[0];
    if (!img || !top) return;
    setConceptRenderingIdx(idx);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/art-director-render`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          vibe_slug: top.vibe_slug,
          recipe_id: top.id,
          product_image_b64: img,
          product_name: conceptsResult?.product?.name || '',
          brand_id: brandId.trim() || undefined,
          steer: true,
          art_direction: concept.art_direction,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
      const data = await resp.json();
      setConceptRenders((prev) => ({ ...prev, [idx]: { img: data.image_b64, provider: data.provider, cost: data.cost, steered: !!data.steered } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setConceptRenderingIdx(null);
    }
  }, [conceptsResult, brandId]);

  const ad = result?.art_direction;

  return (
    <div className="min-h-screen bg-surface p-6">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-dark">
            Art Director → Cast <span className="text-dark-muted font-normal text-sm">(dev)</span>
          </h1>
          <Link to="/picker-test" className="text-sm text-primary hover:underline">
            picker-test →
          </Link>
        </div>

        {/* ── input form ─────────────────────────────────────────── */}
        <div className="rounded-2xl border border-surface-dark/30 bg-white p-4 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-dark-muted md:col-span-2">
            Product URL <span className="text-primary font-normal">— scraped for description + image so the art director can SEE the product</span>
            <input value={productUrl} onChange={(e) => setProductUrl(e.target.value)}
              placeholder="https://…/products/…"
              className="mt-1 w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm font-normal text-dark" />
          </label>
          <div className="md:col-span-2 text-[11px] text-dark-muted -mt-1">
            …or fill the fields below manually (all optional when a URL is given):
          </div>
          <label className="text-xs font-semibold text-dark-muted md:col-span-2">
            Product name
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm font-normal text-dark" />
          </label>
          <label className="text-xs font-semibold text-dark-muted md:col-span-2">
            Description
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
              className="mt-1 w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm font-normal text-dark" />
          </label>
          <label className="text-xs font-semibold text-dark-muted">
            Category (optional)
            <input value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm font-normal text-dark" />
          </label>
          <label className="text-xs font-semibold text-dark-muted">
            Brand ID (optional — pulls Brand DNA for tone)
            <input value={brandId} onChange={(e) => setBrandId(e.target.value)} placeholder="uuid"
              className="mt-1 w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm font-normal text-dark" />
          </label>
          <div className="flex items-end gap-3 md:col-span-2">
            <label className="text-xs font-semibold text-dark-muted">
              Cast top-K
              <input type="number" value={topK} min={1} max={20}
                onChange={(e) => setTopK(Math.max(1, Math.min(20, Number(e.target.value) || 8)))}
                className="mt-1 w-24 rounded-lg border border-surface-dark/40 px-3 py-2 text-sm font-normal text-dark" />
            </label>
            <label className="text-xs font-semibold text-dark-muted flex items-center gap-2 cursor-pointer pb-2.5">
              <input type="checkbox" checked={steer} onChange={(e) => setSteer(e.target.checked)}
                className="accent-primary w-4 h-4" />
              Steer render to art direction
            </label>
            <div className="ml-auto flex items-center gap-3">
              <label className="text-xs font-semibold text-dark-muted flex items-center gap-1.5">
                <input type="number" value={conceptsN} min={1} max={8}
                  onChange={(e) => setConceptsN(Math.max(1, Math.min(8, Number(e.target.value) || 4)))}
                  className="w-14 rounded-lg border border-surface-dark/40 px-2 py-2 text-sm font-normal text-dark" />
                concepts
              </label>
              <button onClick={runConcepts} disabled={conceptsRunning || running}
                className="px-5 py-2.5 rounded-full border-2 border-primary text-primary text-sm font-semibold hover:bg-primary/5 disabled:opacity-50">
                {conceptsRunning ? 'Generating…' : `Generate ${conceptsN} concepts`}
              </button>
              <button onClick={runDirect} disabled={running || conceptsRunning}
                className="px-6 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-md shadow-emerald-600/25 hover:bg-emerald-700 disabled:opacity-50">
                {running ? 'Directing…' : 'Direct only (fast)'}
              </button>
              <button onClick={run} disabled={running || conceptsRunning}
                className="px-6 py-2.5 rounded-full border-2 border-primary text-primary text-sm font-semibold hover:bg-primary/5 disabled:opacity-50">
                {running ? '…' : 'Direct + Cast'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-600 mb-4">{error}</div>
        )}

        {ad && (
          <>
            {/* ── art direction card ───────────────────────────── */}
            <div className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/[0.04] to-accent-light/40 p-5 mb-5">
              {result?.scraped && (
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-primary/15">
                  <img src={`data:image/jpeg;base64,${result.scraped.image_b64}`}
                    alt={result.scraped.name}
                    className="w-14 h-14 rounded-lg object-contain bg-white border border-surface-dark/30 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-dark truncate">{result.scraped.name}</div>
                    <div className="text-[11px] text-dark-muted line-clamp-1">{result.scraped.description}</div>
                  </div>
                  {result?.saw_product_image && (
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold whitespace-nowrap">
                      <i className="ri-eye-line" /> art director saw this
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-clapperboard-line text-primary" />
                <h2 className="font-bold text-dark">Art direction</h2>
                <span className="text-xs text-dark-muted">
                  · {ad.setting_type} · energy: {ad.energy}
                  {result?.brand_dna_used ? ' · Brand DNA on' : ''}
                  {result?.pool_size ? ` · pool ${result.pool_size}` : ''}
                </span>
                {ad.family && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold">
                    family: {ad.family}
                  </span>
                )}
              </div>
              <p className="text-sm text-dark mb-3"><b>Scene:</b> {ad.scene}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-dark">
                <div><b>Mood:</b> {(ad.mood || []).join(', ')}</div>
                <div><b>Lighting:</b> {ad.lighting}</div>
                <div><b>Palette:</b> {ad.palette_intent}</div>
                <div><b>Composition:</b> {ad.composition}</div>
              </div>
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                <b>AVOID:</b> {(ad.avoid || []).join(' · ')}
              </div>
              {ad.rationale && <p className="mt-2 text-xs italic text-dark-muted">{ad.rationale}</p>}
            </div>

            {/* ── pair pick (lean architecture) ─────────────────── */}
            {result?.scraped && (
              <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/40 p-5 mb-5">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <i className="ri-flask-line text-emerald-600" />
                  <h2 className="font-bold text-dark">Pair pick <span className="text-emerald-700">(lean)</span></h2>
                  <span className="text-xs text-dark-muted">
                    the AD owns the whole scene — one recipe contributes ONLY its construction device + light-handling
                  </span>
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold">
                    draws from: {ad.family || 'any (no family)'}
                  </span>
                </div>

                {!pairResult ? (
                  <button onClick={() => renderPair(false)} disabled={pairRunning}
                    className="mt-3 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-md shadow-emerald-600/25 hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
                    {pairRunning
                      ? (<><i className="ri-loader-4-line animate-spin" /> Picking pair + rendering…</>)
                      : (<><i className="ri-image-add-line" /> Render with my product (pair)</>)}
                  </button>
                ) : (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-[minmax(0,340px)_1fr] gap-4">
                    <div className="relative aspect-square rounded-xl overflow-hidden bg-white border border-surface-dark/30">
                      <img src={`data:image/png;base64,${pairResult.img}`} alt="pair render" className="w-full h-full object-cover" />
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">your product</div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold">family: {pairResult.family}</span>
                        {pairResult.pair.fallback && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">fallback: from {pairResult.pair.picked_from_family}</span>
                        )}
                        <span className="text-[10px] text-dark-muted">{pairResult.provider} · ${pairResult.cost?.toFixed(4)}</span>
                      </div>
                      <div className="rounded-lg bg-white border border-surface-dark/30 p-3 mb-2">
                        <div className="text-[10px] uppercase tracking-wide text-dark-muted mb-0.5">Construction device</div>
                        <div className="text-sm font-bold text-dark">{pairResult.pair.device_name}</div>
                        <div className="text-xs text-dark leading-snug mt-0.5">{pairResult.pair.device_desc}</div>
                      </div>
                      <div className="rounded-lg bg-white border border-surface-dark/30 p-3 mb-2">
                        <div className="text-[10px] uppercase tracking-wide text-dark-muted mb-0.5">Light-handling</div>
                        <div className="text-xs text-dark leading-snug">{pairResult.pair.light_handling || '(none)'}</div>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        {pairResult.pair.preview_url && (
                          <img src={pairResult.pair.preview_url} alt="" className="w-12 h-12 rounded object-cover border border-surface-dark/30" loading="lazy" decoding="async" />
                        )}
                        <div className="text-[11px] text-dark-muted">
                          source recipe: <span className="font-semibold text-dark">{pairResult.pair.source_vibe}</span> · {pairResult.pair.source_id}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => renderPair(true)} disabled={pairRunning}
                          className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                          {pairRunning ? (<><i className="ri-loader-4-line animate-spin" /> …</>) : (<><i className="ri-shuffle-line" /> Re-roll pair</>)}
                        </button>
                        <button onClick={() => renderPair(false)} disabled={pairRunning}
                          className="px-4 py-1.5 rounded-lg bg-emerald-600/10 text-emerald-700 text-xs font-semibold hover:bg-emerald-600/20 disabled:opacity-50 inline-flex items-center gap-1.5">
                          <i className="ri-refresh-line" /> New pick
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── cast grid (only when Direct + Cast was run) ───── */}
            {(result?.cast?.length ?? 0) > 0 && (
            <>
            <h2 className="font-bold text-dark mb-3">Cast — {result?.cast.length} recipes fulfilling the direction</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {result?.cast.map((c, i) => (
                <div key={c.id} className="rounded-2xl border border-surface-dark/30 bg-white overflow-hidden">
                  <div className="relative aspect-square bg-surface">
                    {renders[c.id] ? (
                      <img src={`data:image/png;base64,${renders[c.id].img}`} alt="rendered"
                        className="w-full h-full object-cover" />
                    ) : c.preview_url ? (
                      <img src={c.preview_url} alt={c.id}
                        className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-dark-muted/40">
                        <i className="ri-image-line text-3xl" />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-bold">
                      #{i + 1} · fit {c.fit ?? '—'}
                    </div>
                    {renders[c.id] && (
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                        your product
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1.5 text-xs text-dark-muted mb-1">
                      <span className="font-semibold text-dark">{c.vibe}</span>
                      <span className="px-1.5 py-0.5 rounded bg-surface-dark/40 text-[10px]">
                        {(c.product_types || []).join(', ') || 'untagged'}
                      </span>
                      {c.requires_model && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px]">
                          {c.model_relation || 'model'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-dark leading-snug">{c.why}</p>
                    {result?.scraped && (
                      <button onClick={() => renderPick(c)} disabled={renderingId === c.id}
                        className="mt-2 w-full px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                        {renderingId === c.id ? (
                          <><i className="ri-loader-4-line animate-spin" /> Rendering…</>
                        ) : renders[c.id] ? (
                          <><i className="ri-refresh-line" /> Re-render</>
                        ) : (
                          <><i className="ri-image-add-line" /> Render with my product</>
                        )}
                      </button>
                    )}
                    {renders[c.id] && (
                      <div className="mt-1 text-[10px] text-dark-muted text-center">
                        {renders[c.id].steered ? '✨ steered' : 'verbatim'} · {renders[c.id].provider} · ${renders[c.id].cost?.toFixed(4)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </>
            )}
          </>
        )}

        {conceptsResult && (
          <>
            <div className="flex items-center gap-2 mb-3 mt-2">
              <i className="ri-stack-line text-primary" />
              <h2 className="font-bold text-dark">{conceptsResult.concepts.length} concepts — the production shape</h2>
              <span className="text-xs text-dark-muted">
                each a distinct angle, auto-cast to its own best template
                {conceptsResult.brand_dna_used ? ' · Brand DNA on' : ''} · pool {conceptsResult.pool_size}
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {conceptsResult.concepts.map((concept, idx) => {
                const cad = concept.art_direction;
                const top = concept.cast[0];
                const r = conceptRenders[idx];
                return (
                  <div key={idx} className="rounded-2xl border border-surface-dark/30 bg-white overflow-hidden flex">
                    <div className="relative w-44 flex-shrink-0 aspect-square bg-surface">
                      {r ? (
                        <img src={`data:image/png;base64,${r.img}`} alt="rendered" className="w-full h-full object-cover" />
                      ) : top?.preview_url ? (
                        <img src={top.preview_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-dark-muted/40"><i className="ri-image-line text-2xl" /></div>
                      )}
                      {r && <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">your product</div>}
                    </div>
                    <div className="p-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-dark text-sm">{cad.angle || `Concept ${idx + 1}`}</span>
                        <span className="text-[10px] text-dark-muted">{cad.setting_type} · {cad.energy}</span>
                      </div>
                      <p className="text-xs text-dark leading-snug mb-1"><b>Scene:</b> {cad.scene}</p>
                      <p className="text-[11px] text-dark-muted mb-1">mood: {(cad.mood || []).join(', ')}</p>
                      {top && (
                        <p className="text-[11px] text-dark-muted mb-2">
                          → <span className="font-semibold text-dark">{top.vibe}</span> · fit {top.fit}: {top.why}
                        </p>
                      )}
                      {conceptsResult.scraped && top && (
                        <button onClick={() => renderConcept(idx, concept)} disabled={conceptRenderingIdx === idx}
                          className="w-full px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                          {conceptRenderingIdx === idx ? (
                            <><i className="ri-loader-4-line animate-spin" /> Rendering…</>
                          ) : r ? (
                            <><i className="ri-refresh-line" /> Re-render</>
                          ) : (
                            <><i className="ri-image-add-line" /> Render this concept</>
                          )}
                        </button>
                      )}
                      {r && (
                        <div className="mt-1 text-[10px] text-dark-muted text-center">
                          {r.steered ? '✨ steered' : 'verbatim'} · {r.provider} · ${r.cost?.toFixed(4)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
